import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { FormField } from '../components/ui/FormField';
import { AuthLayout, AuthBenefit } from '../components/layout/AuthLayout';
import { AuthFormError, authErrorMessage } from '../components/layout/AuthFormError';
import { forwardState, returnPathFrom } from '../utils/authRedirect';

const BENEFITS: AuthBenefit[] = [
  { icon: 'orders', label: 'Seus pedidos em um só lugar', description: 'Veja o status e o próximo passo de cada compra.' },
  { icon: 'heart', label: 'Favoritos guardados', description: 'As peças que você marcou ficam salvas na sua conta.' },
  { icon: 'star', label: 'Avalie o que comprou', description: 'Conte o que achou das peças quando o pedido for finalizado.' },
];

const CREDENTIAL_ERROR = 'E-mail, celular ou senha não conferem. Confira e tente de novo.';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);

  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!credential.trim() || !password) {
      setError('Preencha o e-mail ou celular e a senha.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await authService.login({ credential: credential.trim(), password });
      login(data.accessToken, data.customerId, data.name, data.canManageStoreSettings);
      navigate(returnPathFrom(location.state), { replace: true });
    } catch (err) {
      setError(authErrorMessage(err, 'Não foi possível entrar agora. Tente de novo.', CREDENTIAL_ERROR));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      heading={<>Que bom te ver <span className="text-chocolate">de novo</span></>}
      description="Entre para acompanhar seus pedidos e encontrar suas peças favoritas."
      benefits={BENEFITS}
    >
      <div className="mb-7">
        <h1 className="font-display text-3xl font-bold text-text-dark">Entrar</h1>
        <p className="text-text-medium text-base mt-1">Use o e-mail ou o celular cadastrado.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField label="E-mail ou celular">
          <Input
            type="text"
            inputMode="email"
            value={credential}
            onChange={(e) => {
              setCredential(e.target.value);
              setError('');
            }}
            placeholder="seu@email.com ou 85999999999"
            autoComplete="username"
            autoCapitalize="none"
          />
        </FormField>

        <div className="flex flex-col gap-1.5">
          <FormField label="Senha">
            <PasswordInput
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              autoComplete="current-password"
            />
          </FormField>
          <Link
            to="/esqueci-senha"
            className="self-end py-1 text-sm font-medium text-chocolate-dark hover:underline underline-offset-4"
          >
            Esqueci minha senha
          </Link>
        </div>

        {error && (
          <AuthFormError>
            <p>{error}</p>
            {error === CREDENTIAL_ERROR && (
              <Link to="/esqueci-senha" className="mt-1 inline-block font-medium text-chocolate-dark underline underline-offset-4">
                Recuperar a senha
              </Link>
            )}
          </AuthFormError>
        )}

        <Button type="submit" fullWidth loading={loading} size="lg">
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>

      <p className="text-center text-base text-text-medium mt-7">
        Ainda não tem conta?{' '}
        <Link to="/cadastro" state={forwardState(location.state)} className="text-chocolate-dark font-semibold hover:underline underline-offset-4">
          Criar conta
        </Link>
      </p>
    </AuthLayout>
  );
}
